import { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Loader2, Edit } from 'lucide-react';

import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { usePartner } from '@/hooks/use-partner';
import Create from './create';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useAuth } from '@/contexts/auth';
import { hasPermission } from '@/utils/permissions';

export default function BrandsSectorsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(() => ({
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: debouncedSearchTerm || undefined
  }), [currentPage, debouncedSearchTerm]);

  const { partners, total, loading, error, refetch } = usePartner(queryParams);
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'edit_content');

  const currentItems = partners || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-end mb-6">
        <div className="relative w-full sm:w-auto">
          <Input
            placeholder="Procurar"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full sm:w-64"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 stroke-gray-400 w-4 h-4" />
        </div>
        <PermissionGuard action="create_content">
          <Button 
            type="button"
            className="flex items-center gap-2 bg-black text-white hover:bg-gray-900 w-full sm:w-auto"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus className="w-4 h-4" />
            Novo
          </Button>
        </PermissionGuard>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Carregando parceiros...</span>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-red-500">Erro ao carregar dados: {error}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Marcas</TableHead>
              <TableHead>Setor</TableHead>
              {canEdit && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 3 : 2} className="text-center py-8 text-gray-500">
                  Nenhum parceiro encontrado
                </TableCell>
              </TableRow>
            ) : (
              currentItems.map((partner: any) => (
                <TableRow key={partner.id} className="h-[60px]">
                  <TableCell className="font-medium">{partner.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{partner.area}</Badge>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <button className="p-1 rounded-md border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {!loading && !error && currentItems.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onPageChange={setCurrentPage}
        />
      )}
      <Create
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={() => {
          refetch?.();
        }}
      />
    </div>
  );
}
